import React, { useEffect, useMemo, useState } from 'react'

/* Redux */
import Redux from 'src/pages/Warenkorb/Redux'
import { useDispatch } from 'src/redux/hooks'

/* Material */
import Typo from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import { pageBuilderButtonSx } from 'src/theme/pageBuilderButtons'
import useStyles from './styles'

/* Functions */
import getTotals from 'src/functions/getTotals'
import formatEuro from 'src/functions/formatEuro'
import { checkCoupon } from 'src/functions/checkCoupon'
import { getCartQuantityDiscountInfos } from 'src/functions/cartQuantityDiscountInfos'
import { getFreightShippingCost, hasFreightShippingItems } from 'src/functions/shippingAvailability'
import { refreshCartItemsCommerce } from 'src/functions/refreshCartItemsCommerce'
import type { tcouponFrontend } from 'src/shared/frontendTypes/coupon'
import type { tItems } from 'src/shared/frontendTypes/warenkorb'

/* Config */
import config from 'src/config'

/* Components */
import VersandkostenHinweis from './VersandkostenHinweis'
import MindermengenzuschlagHinweis from './MindermengenzuschlagHinweis'

/* Next-Dynamic */
import dynamic from 'next/dynamic'

// CountdownClock Konstante
const CountdownClock = dynamic(() => import('src/components/CountdownClock/TextClock'), {
  ssr: false,
})

const { mwst: globalMwst, couponDisplay } = config
const shopId = config.shop.shop

type DisplayableCoupon = tcouponFrontend & { code: string; balance: string }

const isDisplayableCoupon = (couponValue: unknown): couponValue is DisplayableCoupon =>
  Boolean(
    couponValue
    && typeof couponValue === 'object'
    && 'code' in couponValue
    && 'balance' in couponValue
    && typeof couponValue.code === 'string'
    && couponValue.code.trim()
    && typeof couponValue.balance === 'string'
    && couponValue.balance.trim(),
  )

const isCouponUsableForShop = (
  couponValue: unknown,
  currentShopId: number,
): couponValue is DisplayableCoupon =>
  isDisplayableCoupon(couponValue)
  && Array.isArray(couponValue.shops)
  && couponValue.shops.includes(currentShopId)

const getCartRefreshKey = (
  items: tItems,
  coupon: string | null,
  couponObjekt: tcouponFrontend | null,
) => JSON.stringify({
  coupon: coupon || '',
  couponObjekt: couponObjekt
    ? {
      code: couponObjekt.code || '',
      balance: couponObjekt.balance || '',
    }
    : null,
  items: Object.values(items)
    .map(item => ({
      id: item.id,
      key: item.key,
      qty: item.qty,
    }))
    .sort((a, b) => a.key.localeCompare(b.key)),
})

interface Props {
  displayLandMwst?: boolean
  displayVersandkostenHinweis?: boolean
}

const Zusammenfassung = ({
  displayLandMwst,
  displayVersandkostenHinweis,
}: Props) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const { items, shippingCountry, applyCoupon, coupon, couponObjekt } = Redux()

  const [couponInput, setCouponInput] = useState(coupon || '')
  const [couponError, setCouponError] = useState<string | null>(null)
  const [isCouponValid, setIsCouponValid] = useState<boolean>(Boolean(coupon))
  const [couponData, setCouponData] = useState<any | null>(null) // Typ ggf. anpassen
  const cartRefreshKey = useMemo(
    () => getCartRefreshKey(items, coupon, couponObjekt),
    [coupon, couponObjekt, items],
  )

  useEffect(() => {
    if (!items || Object.keys(items).length === 0) return

    let cancelled = false

    const refreshCartItems = async () => {
      const refreshedItems = await refreshCartItemsCommerce(items)

      if (cancelled || JSON.stringify(refreshedItems) === JSON.stringify(items)) return

      dispatch({
        type: 'restoreWarenkorbItems',
        payload: {
          items: refreshedItems,
          coupon,
          couponObjekt,
        },
      })
    }

    void refreshCartItems()

    return () => {
      cancelled = true
    }
  // Der Live-Commerce-Refresh darf nicht auf jedes intern neu gemergte items-Objekt reagieren.
  }, [cartRefreshKey, coupon, couponObjekt, dispatch])

  useEffect(() => {
    setCouponInput(coupon || '')

    if (coupon) {
      checkCoupon(coupon, shopId)
        .then(data => {
          if (data) {
            setCouponData(data)
            setIsCouponValid(true)
            const currentCouponMatches = isCouponUsableForShop(couponObjekt, shopId)
              && couponObjekt.code === data.code
              && couponObjekt.balance === data.balance

            if (!currentCouponMatches) {
              applyCoupon(data.code || coupon, data)
            }
          } else {
            applyCoupon(null, null)
            setCouponData(null)
            setIsCouponValid(false)
          }
        })
        .catch(() => {
          applyCoupon(null, null)
          setCouponData(null)
          setIsCouponValid(false)
        })
    } else {
      setCouponData(null)
      setCouponError(null)
      setIsCouponValid(false)
    }
  }, [coupon, couponObjekt])

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      setCouponError('Bitte geben Sie einen Gutschein-Code ein')
      setIsCouponValid(false)
      return
    }
    try {
      const data = await checkCoupon(couponInput, shopId)
      if (data) {
        applyCoupon(data.code || couponInput.trim(), data)
        setCouponData(data)
        setCouponError(data.slogan || null)
        setIsCouponValid(true)
      } else {
        applyCoupon(null, null)
        setCouponData(null)
        setCouponError('Ungültiger Gutschein-Code')
        setIsCouponValid(false)
      }
    } catch (error: any) {
      applyCoupon(null, null)
      setCouponData(null)
      setCouponError(error.message)
      setIsCouponValid(false)
    }
  }

  const country = (displayLandMwst && shippingCountry) || {
    iso: 'DE',
    eu: true,
    mwst: globalMwst,
  }

  const { eu: isEu, mwst: countryMwst } = country
  const freightShippingOnRequest = hasFreightShippingItems(items)
    && getFreightShippingCost(country.iso) === null
  const activeCouponData = isCouponUsableForShop(couponData, shopId) ? couponData : null
  const activeCouponObjekt = isCouponUsableForShop(couponObjekt, shopId)
    ? couponObjekt
    : activeCouponData || undefined

  const {
    WarenkorbBrutto,
    WarenkorbNetto,
    EnthalteneMwSt,
    Versandkosten,
    Mindermengenzuschlag,
    Rabatt,
    Gesamtsumme,
    Rabattgrundlage,
    brandsTotals
  } = getTotals(items, country, activeCouponObjekt);

  // Zeilen zum Rendern
  interface tRow {
    Titel: string,
    Summe: number,
    Text?: string | JSX.Element,
    Infos?: JSX.Element,
    ZeigeNull?: boolean,
    Styles?: string[],
  }
  
  const formattedWarenkorbRabatt = formatEuro(Rabattgrundlage);
  const quantityDiscountInfos = getCartQuantityDiscountInfos(items)
  const quantityDiscountInfoElement = quantityDiscountInfos.length > 0
    ? (
      <span className={classes.quantityDiscountSummary}>
        {quantityDiscountInfos.map(info => (
          <span key={info.key}>
            {info.name}: {info.progress}
          </span>
        ))}
      </span>
    )
    : undefined
  const quantityDiscountRows: tRow[] = quantityDiscountInfoElement
    ? [{
      Titel: 'Mengenaktion:',
      Summe: 0,
      Text: '',
      Infos: quantityDiscountInfoElement,
      Styles: ['trGreen'],
    }]
    : []

  // Berechne, ob die Uhr angezeigt werden soll (nur wenn <= 48 Stunden übrig sind)
  const couponExpirationDate = activeCouponData?.expirationDate
  const showCountdown =
    couponExpirationDate &&
    new Date(couponExpirationDate).getTime() - new Date().getTime() <= 48 * 60 * 60 * 1000
  const couponCategoriesCount = activeCouponData?.categories?.length || 0
  const couponProductsCount = activeCouponData?.products?.length || 0

  const couponInfos = activeCouponData
    ? (
    <span>
      {!activeCouponData.stackable && (
        <span style={{ display: 'block', color: 'green' }}>
          (Gutschein nicht kombinierbar!)
        </span>
      )}
      {activeCouponData.brand && (
        <span style={{ display: 'block', color: 'green' }}>
          (Coupon für Artikel von {brandsTotals[activeCouponData.brand]?.title})
        </span>
      )}
      {couponCategoriesCount > 0 && (
        <span style={{ display: 'block', color: 'green' }}>
          (Coupon für {couponCategoriesCount} Kategorien)
        </span>
      )}
      {couponProductsCount > 0 && (
        <span style={{ display: 'block', color: 'green' }}>
          (Coupon für {couponProductsCount} Artikel)
        </span>
      )}
      <span style={{ color: 'green' }}>
        {`abzgl. ${activeCouponData.balance} von (${formattedWarenkorbRabatt})`}
      </span>
      {showCountdown && couponExpirationDate && (
        <span
          style={{
            display: 'block',
            marginTop: '8px',
            textAlign: 'left',
            color: 'green',
          }}
        >
          Coupon läuft ab in{' '}
          <CountdownClock
            dateEnd={couponExpirationDate}
            size="small"
            variant="alternative"
          />{' '}
          - Jetzt sichern, bevor er abgelaufen ist!
        </span>
      )}
    </span>
  )
    : undefined
  const couponRows: tRow[] = activeCouponData && couponInfos
    ? [{
      Titel: `Gutschein ${activeCouponData.code}`,
      Summe: Rabatt,
      ZeigeNull: false,
      Infos: couponInfos,
      Styles: ['trFett', 'trGreen'],
    }]
    : []
  const mindermengenzuschlagInfos = Mindermengenzuschlag > 0
    ? <MindermengenzuschlagHinweis summe={WarenkorbBrutto} />
    : undefined

  const rows: tRow[] = isEu
    ? [
        {
          Titel: 'Zwischensumme:',
          Summe: WarenkorbBrutto,
        },
        ...quantityDiscountRows,
        ...couponRows,
        {
          Titel: 'Versandkosten' + (displayVersandkostenHinweis ? ' nach Deutschland:' : ''),
          Summe: Versandkosten,
          Text: freightShippingOnRequest ? 'auf Anfrage' : undefined,
          Infos: displayVersandkostenHinweis ? <VersandkostenHinweis /> : undefined,
        },
        {
          Titel: 'Mindermengenzuschlag:',
          Summe: Mindermengenzuschlag,
          ZeigeNull: false,
          Infos: mindermengenzuschlagInfos,
          Styles: ['trFett'],
        },
        {
          Titel: `Enthaltene MwSt.${country && country.iso !== 'DE' ? ` (${countryMwst}%)` : ''}:`,
          Summe: EnthalteneMwSt,
        },
        {
          Titel: 'Gesamtsumme:',
          Summe: Gesamtsumme,
        },
      ]
    : [
        {
          Titel: 'Zwischensumme:',
          Summe: WarenkorbNetto,
        },
        ...quantityDiscountRows,
        ...couponRows,
        {
          Titel: 'Versandkosten:',
          Summe: Versandkosten,
          Text: freightShippingOnRequest ? 'auf Anfrage' : undefined,
        },
        {
          Titel: 'Mindermengenzuschlag:',
          Summe: Mindermengenzuschlag,
          ZeigeNull: false,
          Infos: mindermengenzuschlagInfos,
          Styles: ['trFett'],
        },
        {
          Titel: 'Enthaltene MwSt.:',
          Summe: 0,
          Styles: ['trFett'],
        },
        {
          Titel: 'Gesamtsumme:',
          Summe: Gesamtsumme,
        },
      ]

  return (
    <>
      {couponDisplay && (
        <TableContainer component={Paper} className={`${classes.couponContainer} ${isCouponValid ? 'valid' : ''}`}>
          <Table aria-label="Coupon Eingabe">
            <TableBody>
              <TableRow>
                <TableCell colSpan={2}>
                  <Typo>Gutschein Code:</Typo>
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value)}
                    className={classes.couponInput}
                  />
                  {couponError && (
                    <Typo variant="body2" className={isCouponValid ? classes.successText : classes.errorText}>
                      {couponError}
                    </Typo>
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Button
                    color="primary"
                    size="large"
                    variant="contained"
                    sx={pageBuilderButtonSx}
                    onClick={handleApplyCoupon}
                    className={`${classes.applyButton} ${isCouponValid ? 'valid' : ''}`}
                  >
                    {isCouponValid ? 'Gutschein eingelöst' : 'Gutschein einlösen'}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TableContainer component={Paper}>
        <Table aria-label="Zusammenfassung vom Warenkorbinhalt">
          <TableBody>
            {rows
              .filter(row => row.ZeigeNull !== false || row.Summe > 0 || row.Text || row.Infos)
              .map(({ Styles = [], Titel, Summe, Text, Infos }, i) => {
                const classNames: string[] = []
                ;(['trFett', 'trRed', 'trGreen'] as const).forEach(style => {
                  if (Styles.includes(style)) classNames.push(classes[style])
                })
                return (
                  <TableRow key={i} className={classNames.join(' ')}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      <Typo>{Titel}</Typo>
                      {Infos}
                    </TableCell>
                    <TableCell align="right">
                      <Typo>{Text !== undefined ? Text : formatEuro(Summe)}</Typo>
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default Zusammenfassung
